/**
 * The shield model (vanilla ShieldModel: a 12x22x1 plate and a 2x6x6 grip on
 * the `shield_base_nopattern` sheet), shared by the first-person view and the
 * third-person body. Geometry is in block units with y down and the front on -z,
 * like every other entity model here.
 */

import * as THREE from 'three';
import { boxGeometry } from './firstPerson';
import { createLitMaterial, setMeshLight } from './itemEntities';
import type { EnvUniforms } from './env';

const SHEET = 'mc/entity/shield/shield_base_nopattern.png';
const DEG = Math.PI / 180;

let sheetTex: THREE.Texture | null = null;
function sheet(): THREE.Texture {
  if (sheetTex) return sheetTex;
  sheetTex = new THREE.TextureLoader().load(SHEET);
  sheetTex.colorSpace = THREE.SRGBColorSpace;
  sheetTex.magFilter = sheetTex.minFilter = THREE.NearestFilter;
  sheetTex.generateMipmaps = false;
  return sheetTex;
}

/** Plate + grip meshes in a group (block units, y down, front -z). */
export function buildShield(env: EnvUniforms, kind: 'hand' | 'world'): THREE.Group {
  const mat = createLitMaterial(env, sheet(), kind, true);
  const g = new THREE.Group();
  const plate = new THREE.Mesh(boxGeometry(0, 0, -6, -11, -2, 12, 22, 1, 0, 64, 64), mat);
  const grip = new THREE.Mesh(boxGeometry(26, 0, -1, -3, -1, 2, 6, 6, 0, 64, 64), mat);
  for (const m of [plate, grip]) { m.frustumCulled = false; g.add(m); setMeshLight(m, 1); }
  return g;
}

/** Set the brightness of every mesh in a shield group. */
export function lightShield(g: THREE.Group, light: number): void {
  for (const c of g.children) setMeshLight(c as THREE.Mesh, light);
}

/** vanilla item/shield.json + shield_blocking.json first-person display: [rotation deg, translation px], scale 1.25. */
const DISPLAY = {
  right: { idle: [[0, 180, 5], [-10, 2, -10]], block: [[0, 180, -5], [-15, 5, -11]] },
  // ItemTransform.apply negates a left hand's x translation and Y/Z rotation, so these are the JSON values after that flip
  left: { idle: [[0, 180, -5], [-10, 0, -10]], block: [[0, 180, 5], [-5, 5, -11]] },
} as const;

/**
 * First-person shield, posed the vanilla way (ItemInHandRenderer): arm transform
 * translate(side*0.56, -0.52, -0.72), then the model's firstperson display transform
 * (idle or blocking, eased by the raise), scale 1.25, the item-space -0.5 offset
 * and the shield renderer's scale(1,-1,-1).
 */
export class ShieldView {
  readonly object3d = new THREE.Group();
  private readonly model: THREE.Group;
  /** 0 = lowered, 1 = fully raised. */
  private raise = 0;
  private readonly m = new THREE.Matrix4();

  constructor(env: EnvUniforms) {
    this.model = buildShield(env, 'hand');
    for (const c of this.model.children) (c as THREE.Mesh).renderOrder = 1003;
    this.model.matrixAutoUpdate = false;
    this.object3d.add(this.model);
    this.object3d.visible = false;
  }

  update(dt: number, s: { has: boolean; blocking: boolean; hidden: boolean; light: number }): void {
    const show = s.has && !s.hidden;
    this.object3d.visible = show;
    if (!show) { this.raise = 0; return; }
    const target = s.blocking ? 1 : 0;
    this.raise += (target - this.raise) * Math.min(1, dt * 20);
    const r = this.raise;
    // always the off-hand (left) display: vanilla keeps the shield on the left
    const d = DISPLAY.left;
    const lerp = (a: readonly number[], b: readonly number[], i: number): number => a[i] + (b[i] - a[i]) * r;
    const rot = [0, 1, 2].map((i) => lerp(d.idle[0], d.block[0], i) * DEG);
    const tr = [0, 1, 2].map((i) => lerp(d.idle[1], d.block[1], i) / 16);
    const side = -1;
    const m = this.m.identity();
    m.multiply(new THREE.Matrix4().makeTranslation(side * 0.56, -0.52, -0.72));
    m.multiply(new THREE.Matrix4().makeTranslation(tr[0], tr[1], tr[2]));
    m.multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(rot[0], rot[1], rot[2], 'XYZ')));
    m.multiply(new THREE.Matrix4().makeScale(1.25, 1.25, 1.25));
    m.multiply(new THREE.Matrix4().makeTranslation(-0.5, -0.5, -0.5));
    m.multiply(new THREE.Matrix4().makeScale(1, -1, -1));
    this.model.matrix.copy(m);
    this.model.matrixWorldNeedsUpdate = true;
    lightShield(this.model, Math.max(s.light, 0.3));
  }
}
